/**
 * CallbackCell - the lead's next booked callback (docs/plans/crm-recruitment-list-plan.md Part A
 * section 4, B4): its time ("3:15 PM", "tomorrow 9:00 AM"), marked when it is overdue, and whose
 * call it is when that is not the viewer. With none booked, a "Book" link opens the attempt menu
 * straight at "Call them back at...", which books a real callback (it shows on Today, the
 * calendar and the reminders) through the same attempt route as the menu.
 */
import { useCallback, useMemo } from 'react';
import { CalendarClock } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { dateTime, useTeam } from '../../../lib';
import { contactName } from '../../../types';
import { useNow } from '../../../useNow';
import { clockWords } from '../../../workQueueLogic';
import AttemptMenu from '../../AttemptMenu';
import type {
  RecruitAttemptOutcome,
  RecruitAttemptRequest,
  RecruitAttemptResponse,
  RecruitRow,
  RecruitRowChangeHandler,
  RecruitStudy,
} from '../../types';
import { applyAttemptEffect, triesUnavailableReason } from './AttemptsCell';

export interface CallbackCellProps {
  row: RecruitRow;
  /** The study the list is for: the attempt route's :studyId and its cadence ceiling. */
  study: Pick<RecruitStudy, 'id' | 'max_attempts'>;
  onRowChange: RecruitRowChangeHandler;
  className?: string;
}

export function CallbackCell({ row, study, onRowChange, className = '' }: CallbackCellProps) {
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const { byId } = useTeam();
  const nowMs = useNow(15000);
  const contactId = row.contact.id;
  const cb = row.callback;

  const onLogged = useCallback((outcome: RecruitAttemptOutcome, res: RecruitAttemptResponse, request: RecruitAttemptRequest) => {
    onRowChange(contactId, (r) => applyAttemptEffect(r, outcome, res, request, meId));
  }, [contactId, meId, onRowChange]);

  const shown = useMemo(() => {
    if (!cb) return null;
    const now = new Date(nowMs);
    const t = Date.parse(cb.scheduled_at);
    const overdue = !Number.isNaN(t) && t < now.getTime();
    const when = clockWords(cb.scheduled_at, now) || dateTime(cb.scheduled_at);
    const assignee = cb.assignee_user_id;
    const who = assignee && assignee !== meId
      ? (cb.assignee_name || byId.get(assignee)?.name || 'a teammate')
      : null;
    return { overdue, when, who };
  }, [cb, nowMs, meId, byId]);

  if (cb && shown) {
    return (
      <span
        title={`Call back ${dateTime(cb.scheduled_at)}${shown.who ? `, ${shown.who}'s call` : ''}`}
        className={`flex min-w-0 items-center gap-1 text-xs ${
          shown.overdue ? 'font-medium text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-200'
        } ${className}`}
      >
        <CalendarClock className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">{shown.overdue ? `Overdue, ${shown.when}` : shown.when}</span>
        {shown.who && <span className="truncate font-normal text-gray-400 dark:text-gray-500">· {shown.who}</span>}
      </span>
    );
  }

  if (triesUnavailableReason(row)) {
    return <span className={`text-sm text-gray-400 dark:text-gray-500 ${className}`}>—</span>;
  }

  return (
    <span className={`inline-flex ${className}`}>
      <AttemptMenu
        variant="book"
        studyId={study.id}
        contactId={contactId}
        surface="sheet"
        maxAttempts={study.max_attempts}
        onLogged={onLogged}
        name={contactName(row.contact)}
      />
    </span>
  );
}

export default CallbackCell;
